import './BubbleButton.scss';

import React, { CSSProperties } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { IconProp, RotateProp, SizeProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface BubbleButtonPropsBase {
  variant:
    | 'slate'
    | 'velvet'
    | 'navy'
    | 'titanium'
    | 'crimson'
    | 'smoke'
    | 'white'
    | 'banana'
    | 'ocean'
    | 'lime'
    | 'grape'
    | 'dirt'
    | 'forest'
    | 'okta'
    | 'emerald';
  size?: 'medium' | 'large' | 'small';
  appearance?: 'rounded' | 'squared' | 'triangular' | 'circled';
  inverse?: boolean;
  block?: boolean;
  icon?: IconProp;
  iconSuffix?: IconProp;
  iconRotate?: RotateProp;
  iconSize?: SizeProp;
  iconClasses?: string;
  iconStyle?: CSSProperties;
  iconNoMargin?: boolean;
  buttonRef?: React.RefObject<HTMLButtonElement>;
  linkProps?: LinkProps;
  allowPropagation?: boolean;
}

export type BubbleButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  BubbleButtonPropsBase;

export default function BubbleButton(props: BubbleButtonProps) {
  const {
    // from component
    variant,
    size,
    appearance,
    inverse,
    block,
    buttonRef,
    linkProps,
    icon,
    iconSize,
    iconNoMargin,
    iconSuffix,
    iconRotate,
    allowPropagation,
    iconClasses: propIconClasses,
    iconStyle: propIconStyle,
    // from div
    className: propClasses,
    style: propStyle,
    onClick: propOnClick,
    onBlur: propOnBlur,
    onFocus: propOnFocus,
    onDoubleClick: propOnDoubleClick,
    children,
    ...rest
  } = props;

  const classes = ['bubble-button whitespace-nowrap'];
  if (variant) classes.push(variant);
  if (size) classes.push(size);
  if (appearance) classes.push(appearance);
  if (inverse) classes.push('inverse');
  if (block) classes.push('full');
  if (linkProps) classes.push('relative');
  if (propClasses) classes.push(propClasses);
  if (props.disabled) classes.push('pointer-events-none opacity-75');

  let style: React.CSSProperties = {};
  if (propStyle) style = { ...style, ...propStyle };

  const iconClasses = [!children || iconNoMargin ? '' : size === 'small' ? 'mr-1' : 'mr-2'];
  if (propIconClasses) iconClasses.push(propIconClasses);

  const iconSuffixClasses = [children && !iconNoMargin ? 'ml-2' : ''];
  if (propIconClasses) iconSuffixClasses.push(propIconClasses);

  const buttonContent = (
    <div className="button-content">
      {icon && (
        <FontAwesomeIcon
          icon={icon}
          rotation={iconRotate}
          fixedWidth
          size={iconSize}
          className={iconClasses.join(' ')}
          style={propIconStyle}
        />
      )}
      {children}
      {iconSuffix && (
        <FontAwesomeIcon
          icon={iconSuffix}
          rotation={iconRotate}
          fixedWidth
          size={iconSize}
          className={iconSuffixClasses.join(' ')}
        />
      )}
    </div>
  );

  function handleOnClick(evt: React.MouseEvent<HTMLButtonElement>) {
    if (!allowPropagation) {
      evt.stopPropagation();
    }

    if (propOnClick) {
      propOnClick(evt);
    }
  }

  function handleOnDoubleClick(evt: React.MouseEvent<HTMLButtonElement>) {
    if (!allowPropagation) {
      evt.stopPropagation();
    }

    if (propOnDoubleClick) {
      propOnDoubleClick(evt);
    }
  }

  function handleOnBlur(evt: React.FocusEvent<HTMLButtonElement>) {
    if (!allowPropagation) {
      evt.stopPropagation();
    }

    if (propOnBlur) {
      propOnBlur(evt);
    }
  }

  function handleOnFocus(evt: React.FocusEvent<HTMLButtonElement>) {
    if (!allowPropagation) {
      evt.stopPropagation();
    }

    if (propOnFocus) {
      propOnFocus(evt);
    }
  }

  return (
    <button
      {...rest}
      type={props.type || 'button'}
      ref={buttonRef}
      className={classes.join(' ')}
      style={style}
      onClick={handleOnClick}
      onBlur={handleOnBlur}
      onFocus={handleOnFocus}
      onDoubleClick={handleOnDoubleClick}
    >
      {linkProps ? <Link {...linkProps}>{buttonContent}</Link> : <>{buttonContent}</>}
    </button>
  );
}
